import * as Sentry from '@sentry/angular';
// Side-effect import: sets `window.rd_version` so the release tag below
// (and any other consumer) has the current commit SHA available.
import './git-revision';

export function initSentry(tenant: string, appEnv: string): void {
  // Don't log app errors on local environment to Sentry.
  if (appEnv === 'local') {
    return;
  }

  Sentry.init({
    dsn: 'https://8aef319cbc22e550bfdd43307eeb1b90@o4507248894541824.ingest.us.sentry.io/4508362643406848',
    ...(window
      ? {
          // @ts-ignore
          release: `${tenant} - ${window.rd_version}`,
        }
      : {}),
    environment: appEnv,

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: 0.05,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    replaysOnErrorSampleRate: appEnv === 'production' ? 0.1 : 0.05,

    // record all sessions in development
    // but only 10% in production
    replaysSessionSampleRate: appEnv === 'production' ? 0.02 : 0,
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 0.01,

    // You can remove this option if you're not planning to use the Sentry Session Replay feature:
    integrations: [
      Sentry.replayIntegration({
        // Additional Replay configuration goes in here, for example:
        // do not mask text and media on staging to make debugging easier
        maskAllText: appEnv === 'production' ? true : false,
        blockAllMedia: appEnv === 'production' ? true : false,
        // only log network requests on staging
        networkDetailAllowUrls:
          appEnv === 'production' ? undefined : [window.location.host],
        networkDetailDenyUrls: [],
      }),
      Sentry.browserTracingIntegration({
        enableInp: true,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    // send PII in staging and for debugging
    // DO NOT ENABLE THIS IN PRODUCTION
    // https://docs.sentry.io/platforms/javascript/configuration/integrations/httpclient/#senddefaultpii
    sendDefaultPii: appEnv !== 'production',
    attachStacktrace: true,
    // WebKit aborts in-flight XHRs when the page is hidden, so a user who
    // navigates away mid-bootstrap cancels the i18n asset request. That
    // surfaces as an HttpErrorResponse with status 0 (request never
    // completed) and is pure noise — the user is already gone. Matched on
    // the original throw because Sentry serializes HttpErrorResponse into
    // an "Object captured as exception" event, so `ignoreErrors` can't see
    // it. Scoped to assets/i18n so genuine API network/CORS failures, and
    // real 404s on a bad deploy, still report.
    beforeSend(event, hint) {
      const error = hint?.originalException as
        | { status?: number; url?: unknown }
        | undefined;

      if (
        error?.status === 0 &&
        typeof error.url === 'string' &&
        error.url.includes('assets/i18n/')
      ) {
        return null;
      }

      return event;
    },
    ignoreErrors: [
      // https://kaligo.atlassian.net/browse/RD-5027
      // prettier-ignore
      "document\.getElementsByClassName\('page-title'\)\[0\]",
      'Error retrieving icon',
      // prettier-ignore
      'Uncaught \(in promise\): Event: {"isTrusted":true}',
      // prettier-ignore
      "Uncaught \(in promise\): InvalidStateError: Failed to execute 'transaction' on 'IDBDatabase'",
      'HM07 Invalid token expiry',
      'HM08 Timeout',
      'HM09 Missing refresh token from localStorage',
      'GH1003 Invalid token: Nil JSON web token',
      "Can't find variable keysharp_webview_start",
      'Item sent with null or missing arguments',
      'Error response received for message <get-frame-manager-configuration>',
      'EmptyError: no elements in sequence',
      // prettier-ignore
      'Failed to load Stripe\.js',
      // prettier-ignore
      "Cannot read properties of undefined \(reading 'innerText'\)",
      'SecurityError: The operation is insecure.',
      'Error: Error retrieving icon :icon-',
      // prettier-ignore
      'sa\(\)\.runtime\.sendNativeMessage is not a function',
      // prettier-ignore
      'Error: Uncaught \(in promise\): Error: Failed to load Stripe\.js',
      // prettier-ignore
      'Cannot read property of undefined/null expression ifameElement.contentDocument',
      // prettier-ignore
      'Can\'t find variable GetHTMLElementsAtPoint',
      "Can't find variable BCHandleOnLoad",
      "Can't find variable: BCHandleOnLoad",
      // prettier-ignore
      "Can\'t find variable BCHandleOnLoad",
      // prettier-ignore
      "Can\'t find variable: BCHandleOnLoad",
      'BCHandleOnLoad is not defined',
      // prettier-ignore
      'Error: Failed to execute \'invoke\' on \'CreateURLCallback\':',
      // prettier-ignore
      'Missing refresh token from localStorage',
      'Hermes Invalid Token Error',
      // prettier-ignore
      'Could not download source map specified in the minified js file',
      'HermesAuthError: [ANZ]',
      'HermesAuthError: [BNZ]',
      'The operation is insecure',
      'NG0908',
      'Cannot read property of undefined/null expression this.optionsProvider',
      'window.requestIdleCallback is not a function',
      'ethereum',
      'signal is aborted without reason',
      "Can't find variable fbq",
      '[alloy]',
      'BOQ is not defined',
    ],
  });

  if (appEnv === 'staging') {
    Sentry.addIntegration(
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        showEmail: false,
      }),
    );
  }
}
